import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ExternalLink } from "lucide-react";
import { hqApi } from "@/hq/hqApi";
import { asText } from "@/hq/asText";

/**
 * Phase 3I.4U — Interested drill-down drawer.
 *
 * Powered by /api/hq/control/analytics/interested-prospects. The row
 * count returned here ALWAYS equals the KPI value (same unique-business
 * predicate). Business click routes to the existing Client Profile;
 * pitch cell opens the existing PitchDetailModal — no duplicate detail
 * systems.
 *
 * Two variants:
 *   variant="all"            → all currently Interested prospects
 *   variant="follow_up"      → source=follow_up_email
 *   variant="outreach:{stage}" → outreach-journey stage drill-down
 */
export default function InterestedProspectsDrawer({ open, variant = "all", days = 0,
                                                     stage = null, source = "all",
                                                     onClose, onOpenPitch }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const [srcFilter, setSrcFilter] = useState(source);

  useEffect(() => { setSrcFilter(source); }, [source, open]);

  useEffect(() => {
    if (!open) return;
    setData(null);
    const isOutreach = variant?.startsWith?.("outreach") && stage;
    const isHealth   = variant === "health_checks";
    const isOrange   = variant === "orange_leads";
    const url = isHealth
      ? `/control/analytics/health-checks`
      : isOrange
        ? `/control/analytics/orange-leads`
        : isOutreach
          ? `/control/analytics/outreach-drilldown`
          : `/control/analytics/interested-prospects`;
    const params = isHealth
      ? { days: days || 0 }
      : isOrange
        ? { source: srcFilter || "all", days: days || 0, q, sort }
        : isOutreach
          ? { stage, days: days || 30 }
          : { source: variant === "follow_up" ? "follow_up_email" : "any", days: days || 0 };
    hqApi.get(url, { params })
      .then((r) => setData(r.data))
      .catch((e) => setData({ error: e.response?.data?.detail || "Failed to load" }));
  }, [open, variant, days, stage, srcFilter, q, sort]);

  const title = useMemo(() => {
    if (variant === "orange_leads") {
      const suffix = srcFilter && srcFilter !== "all"
        ? ` · ${srcFilter.replace(/_/g, " ")}` : "";
      return days ? `Orange Leads${suffix} · last ${days} days` : `Orange Leads${suffix}`;
    }
    if (variant === "health_checks") return days ? `Free Health Checks · last ${days} days` : "Free Health Checks · all time";
    if (variant?.startsWith?.("outreach")) {
      const labels = {
        pitches_sent: "Businesses pitched",
        pitches_opened: "Businesses opening pitch",
        pitches_clicked: "Businesses clicking pitch",
        followups_sent: "Businesses sent follow-up",
        followups_opened: "Businesses opening follow-up",
        followup_cta_clicks: "Businesses clicking follow-up CTA",
        interested: "Orange leads · all sources",
        interested_from_followup: "Orange leads · outreach",
      };
      return `${labels[stage] || "Outreach stage"}${days ? ` · last ${days} days` : ""}`;
    }
    return variant === "follow_up" ? "Interested · Follow-Up" : "Interested Prospects";
  }, [variant, stage, srcFilter, days]);

  // Unit label shown next to the total in the drawer header.
  const unitLabel = useMemo(() => {
    if (variant === "health_checks") return { one: "completed scan", many: "completed scans" };
    if (variant === "orange_leads")  return { one: "unique business", many: "unique businesses" };
    // outreach:* — all stages are unique-business by construction
    if (variant?.startsWith?.("outreach")) return { one: "unique business", many: "unique businesses" };
    return { one: "unique prospect", many: "unique prospects" };
  }, [variant]);

  if (!open) return null;

  const rows = data?.items || [];
  const total = data?.total || 0;

  // Reconciliation invariant: the drawer's row count MUST equal the
  // total returned by the backend cohort endpoint (Phase 3I.4W.1).
  // The backend defines the metric AND its drill-down from the SAME
  // predicate — never reconstruct filters on the frontend.
  const invariantOK = rows.length === total || (data && data.error);

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="interested-drawer">
      <div className="absolute inset-0 bg-black/45" onClick={onClose}
           data-testid="interested-drawer-overlay"/>
      <aside
        className="relative w-full max-w-[1080px] bg-white h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right"
        role="dialog" aria-label={title}
      >
        <header className="sticky top-0 z-10 bg-white border-b border-[#EAEAEA] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#FF6A00]">Drill-down</div>
            <h2 className="text-2xl font-black tracking-[-0.02em]" data-testid="interested-drawer-title">
              {title}
            </h2>
            <div className="text-[12px] text-[#8A8A8A] mt-1">
              {data ? (
                <span data-testid="interested-drawer-total">
                  {total} {total === 1 ? unitLabel.one : unitLabel.many}
                </span>
              ) : "Loading…"}
            </div>
          </div>
          <button
            data-testid="interested-drawer-close"
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-[#F6F5F2] grid place-items-center"
            aria-label="Close"
          >
            <X size={18}/>
          </button>
        </header>

        <div className="p-6">
          {variant === "orange_leads" && (
            <div className="flex flex-wrap items-center gap-3 mb-4"
                 data-testid="hq-orange-leads-filters">
              <select
                value={srcFilter}
                onChange={(e) => setSrcFilter(e.target.value)}
                data-testid="hq-orange-leads-source"
                className="h-9 px-3 rounded-full border border-[#EAEAEA] text-[12px] font-bold"
              >
                <option value="all">All sources</option>
                <option value="follow_up_email">Follow-Up Email</option>
                <option value="pitch">Pitch</option>
                <option value="website_contact">Website Contact Form</option>
                <option value="build_my_system">Build My System</option>
                <option value="outreach">Outreach (all)</option>
                <option value="website">Website (all)</option>
              </select>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="hq-orange-leads-sort"
                className="h-9 px-3 rounded-full border border-[#EAEAEA] text-[12px] font-bold"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>
              <input
                type="search"
                placeholder="Search business / contact / email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="hq-orange-leads-search"
                className="h-9 px-4 rounded-full border border-[#EAEAEA] text-[12px] min-w-[260px] flex-1"
              />
            </div>
          )}
          {!data && (
            <div className="space-y-2" data-testid="interested-drawer-loading">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-16 rounded-xl bg-[#F6F5F2] animate-pulse"/>
              ))}
            </div>
          )}

          {data?.error && (
            <div className="p-4 rounded-xl bg-[#FFECEC] text-[#C1350B] text-[13px]">
              {data.error}
            </div>
          )}

          {data && !data.error && rows.length === 0 && (
            <div className="p-6 text-center text-[13px] text-[#8A8A8A]">
              No prospects to show for this segment.
            </div>
          )}

          {data && !data.error && variant === "orange_leads" && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="hq-orange-leads-table">
                <thead>
                  <tr className="border-b border-[#EAEAEA] text-[10px] tracking-[0.14em] uppercase text-[#8A8A8A]">
                    <th className="text-left py-2 pr-3 font-black">Business</th>
                    <th className="text-left py-2 pr-3 font-black">Contact</th>
                    <th className="text-left py-2 pr-3 font-black">Email</th>
                    <th className="text-left py-2 pr-3 font-black">Phone</th>
                    <th className="text-left py-2 pr-3 font-black">Source</th>
                    <th className="text-left py-2 pr-3 font-black">Lead date</th>
                    <th className="text-left py-2 pr-3 font-black">Latest activity</th>
                    <th className="text-right py-2 pr-3 font-black">Pitch</th>
                    <th className="text-right py-2 pr-3 font-black">FU</th>
                    <th className="text-right py-2 pr-3 font-black">Enquiries</th>
                    <th className="text-left py-2 font-black">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.business_id}
                        data-testid={`hq-orange-lead-row-${r.business_id}`}
                        className="border-b border-[#F0EBE3] hover:bg-[#FFF7EE]">
                      <td className="py-2 pr-3">
                        <button type="button"
                          data-testid={`hq-orange-lead-business-${r.business_id}`}
                          onClick={() => { onClose?.(); navigate(`/hq/clients/${r.business_id}`); }}
                          className="font-black hover:text-[#FF6A00] hover:underline text-left">
                          {asText(r.business_name) || "—"}
                        </button>
                      </td>
                      <td className="py-2 pr-3">{asText(r.contact_name) || "—"}</td>
                      <td className="py-2 pr-3">
                        {r.contact_email ? (
                          <a href={`mailto:${r.contact_email}`}
                             className="hover:text-[#FF6A00] hover:underline">
                            {r.contact_email}
                          </a>
                        ) : "—"}
                      </td>
                      <td className="py-2 pr-3">{r.contact_phone || "—"}</td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-wrap gap-1">
                          {(r.sources || []).map((s) => (
                            <span key={s} className={`inline-flex items-center h-5 px-2 rounded-full text-[9px] font-black uppercase tracking-[0.14em] ${
                              (s === "Follow-Up Email" || s === "Pitch" || s === "Auto Reply")
                                ? "bg-[#FFF7EE] text-[#8A2E00] border border-[#FCE1C7]"
                                : "bg-[#EEF7FF] text-[#0A5FBE] border border-[#CFE2F9]"
                            }`}>{s}</span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap text-[#8A8A8A]">
                        {r.first_lead_at ? new Date(r.first_lead_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap text-[#8A8A8A]">
                        {r.latest_activity_at ? new Date(r.latest_activity_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {r.original_pitch?.communication_id ? (
                          <button type="button"
                            data-testid={`hq-orange-lead-pitch-${r.business_id}`}
                            onClick={() => (onOpenPitch || (() => {}))(r.original_pitch.communication_id)}
                            className="hover:text-[#FF6A00] hover:underline">
                            1
                          </button>
                        ) : "—"}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{r.followup_count || 0}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{r.enquiry_count || 0}</td>
                      <td className="py-2 text-[11px] uppercase tracking-[0.12em]">
                        {asText(r.status) || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data && !data.error && variant === "health_checks" && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="hq-hc-drawer-table">
                <thead>
                  <tr className="border-b border-[#EAEAEA] text-[10px] tracking-[0.14em] uppercase text-[#8A8A8A]">
                    <th className="text-left py-2 pr-3 font-black">Website</th>
                    <th className="text-left py-2 pr-3 font-black">Domain</th>
                    <th className="text-right py-2 pr-3 font-black">Orange<br/>score</th>
                    <th className="text-left py-2 pr-3 font-black">Hero status</th>
                    <th className="text-left py-2 pr-3 font-black">Complexity</th>
                    <th className="text-left py-2 pr-3 font-black">Scan date</th>
                    <th className="text-left py-2 pr-3 font-black">Business (if linked)</th>
                    <th className="text-left py-2 font-black">Qualified</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.scan_id} className="border-b border-[#F0EBE3] hover:bg-[#FFF7EE]"
                        data-testid={`hq-hc-drawer-row-${r.scan_id}`}>
                      <td className="py-2 pr-3">
                        <a href={r.submitted_url} target="_blank" rel="noopener noreferrer"
                           className="font-black hover:text-[#FF6A00] hover:underline break-all">
                          {r.submitted_url}
                        </a>
                      </td>
                      <td className="py-2 pr-3 text-[#4A4A4A]">{r.normalized_domain || "—"}</td>
                      <td className="py-2 pr-3 text-right tabular-nums font-black">
                        {r.orange_score ?? "—"}
                      </td>
                      <td className="py-2 pr-3 text-[11px] uppercase tracking-[0.14em]">
                        {(r.hero_status || "").replace(/_/g," ")}
                      </td>
                      <td className="py-2 pr-3 text-[11px]">{r.complexity || "—"}</td>
                      <td className="py-2 pr-3 text-[#8A8A8A] whitespace-nowrap">
                        {r.completed_at ? new Date(r.completed_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2 pr-3">
                        {r.business_id ? (
                          <button type="button"
                                  onClick={() => { onClose?.(); navigate(`/hq/clients/${r.business_id}`); }}
                                  className="text-[#0A0A0A] hover:text-[#FF6A00] hover:underline text-left">
                            {asText(r.business_name) || r.business_id.slice(0,8)}
                          </button>
                        ) : <span className="text-[#8A8A8A]">Anonymous</span>}
                      </td>
                      <td className="py-2">
                        {r.qualified ? (
                          <span className="inline-flex h-5 px-2 rounded-full bg-[#FFF7EE] border border-[#FCE1C7] text-[9px] font-black uppercase tracking-[0.14em] text-[#8A2E00]">Qualified</span>
                        ) : <span className="text-[11px] text-[#8A8A8A]">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-4 text-[11px] text-[#8A8A8A]">
                Free scans are anonymous — no email/contact is captured at scan time.
                Business + contact fields populate only when the scan is later linked
                to a Client. Conversion to Prospect / Optimise / Fix / Client requires
                a canonical scan_id → prospect_id link that isn't yet tracked.
              </p>
            </div>
          )}

          {data && !data.error && variant !== "health_checks" && variant !== "orange_leads" && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="interested-drawer-table">
                <thead>
                  <tr className="border-b border-[#EAEAEA] text-[10px] tracking-[0.14em] uppercase text-[#8A8A8A]">
                    <th className="text-left py-2 pr-3 font-black">Business</th>
                    <th className="text-left py-2 pr-3 font-black">Contact</th>
                    <th className="text-left py-2 pr-3 font-black">Email</th>
                    <th className="text-left py-2 pr-3 font-black">Industry</th>
                    <th className="text-left py-2 pr-3 font-black">Territory</th>
                    <th className="text-left py-2 pr-3 font-black">Interest source</th>
                    <th className="text-left py-2 pr-3 font-black">Interested date</th>
                    <th className="text-left py-2 pr-3 font-black">Original pitch</th>
                    <th className="text-right py-2 pr-3 font-black">Pitch<br/>opens</th>
                    <th className="text-right py-2 pr-3 font-black">Pitch<br/>clicks</th>
                    <th className="text-right py-2 pr-3 font-black">FU<br/>sent</th>
                    <th className="text-right py-2 pr-3 font-black">FU<br/>opens</th>
                    <th className="text-right py-2 pr-3 font-black">FU CTA<br/>clicks</th>
                    <th className="text-left py-2 font-black">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.business_id || i}
                        data-testid={`interested-drawer-row-${r.business_id}`}
                        className="border-b border-[#F0EBE3] hover:bg-[#FFF7EE]">
                      <td className="py-2 pr-3">
                        <button
                          type="button"
                          data-testid={`interested-drawer-business-${r.business_id}`}
                          onClick={() => { onClose?.(); navigate(`/hq/clients/${r.business_id}`); }}
                          className="font-black text-[#0A0A0A] hover:text-[#FF6A00] hover:underline text-left"
                        >
                          {asText(r.business_name) || "—"}
                        </button>
                      </td>
                      <td className="py-2 pr-3">{asText(r.contact_name) || "—"}</td>
                      <td className="py-2 pr-3 text-[#4A4A4A]">
                        {r.contact_email ? (
                          <a href={`mailto:${r.contact_email}`}
                             className="text-[#4A4A4A] hover:text-[#FF6A00] hover:underline">
                            {r.contact_email}
                          </a>
                        ) : "—"}
                      </td>
                      <td className="py-2 pr-3">{asText(r.industry_label || r.industry) || "—"}</td>
                      <td className="py-2 pr-3">{asText(r.territory) || "—"}</td>
                      <td className="py-2 pr-3">
                        <span className={`inline-flex items-center h-5 px-2 rounded-full text-[9px] font-black uppercase tracking-[0.14em] ${
                          r.interest_source === "follow_up_email"
                            ? "bg-[#FFF7EE] text-[#8A2E00] border border-[#FCE1C7]"
                            : "bg-[#F0EBE3] text-[#4A4A4A]"
                        }`}>
                          {r.interest_source === "follow_up_email" ? "Follow-up" :
                           r.interest_source === "admin" ? "Admin" :
                           r.interest_source === "auto_form" ? "Form" :
                           r.interest_source === "auto_reply" ? "Reply" :
                           r.interest_source || "—"}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-[#8A8A8A] whitespace-nowrap">
                        {r.interested_at ? new Date(r.interested_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2 pr-3">
                        {r.original_pitch?.communication_id ? (
                          <button
                            type="button"
                            data-testid={`interested-drawer-pitch-${r.business_id}`}
                            onClick={() => (onOpenPitch || (() => {}))(r.original_pitch.communication_id)}
                            className="text-[#0A0A0A] hover:text-[#FF6A00] hover:underline"
                          >
                            {asText(r.original_pitch.pitch_type) || "Pitch"}
                          </button>
                        ) : "—"}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {r.original_pitch?.open_count ?? 0}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {r.original_pitch?.click_count ?? 0}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {r.followup?.sent_count ?? 0}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {r.followup?.opened_any ?? 0}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {r.followup?.cta_click_any ?? 0}
                      </td>
                      <td className="py-2">
                        <span className="text-[11px] text-[#4A4A4A] uppercase tracking-[0.12em]">
                          {asText(r.status) || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

/** Human labels for public paths shown in Top Pages (Phase 3I.4U). */
export const PUBLIC_PAGE_LABELS = [
  { match: (p) => p === "/",                    name: "Homepage" },
  { match: (p) => /^\/optimise\/?$/.test(p),    name: "Orange Optimise" },
  { match: (p) => /^\/optimise\/report/.test(p),name: "Optimise Report" },
  { match: (p) => /^\/optimise\/continue/.test(p), name: "Optimise Continue" },
  { match: (p) => /#crm-service/.test(p) || p === "/crm", name: "CRM & Business Systems" },
  { match: (p) => /#websites/.test(p) || p === "/websites", name: "Websites" },
  { match: (p) => /#landing/.test(p),           name: "Landing Pages" },
  { match: (p) => /#work/.test(p) || p === "/portfolio", name: "Portfolio" },
  { match: (p) => /#care/.test(p) || p === "/orangecare", name: "OrangeCare" },
  { match: (p) => /#discovery|#enquire/.test(p) || p === "/contact", name: "Contact / Discovery" },
];

export function labelForPath(path) {
  const p = (path || "").split("?")[0];
  const hit = PUBLIC_PAGE_LABELS.find((x) => { try { return x.match(p); } catch (_) { return false; } });
  return hit?.name || null;
}

export function ExternalPageLink({ path, testId }) {
  const label = labelForPath(path);
  return (
    <a
      href={path}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="group inline-flex items-center gap-1.5 max-w-full"
    >
      <span className="font-bold text-[#0A0A0A] group-hover:text-[#FF6A00] group-hover:underline truncate">
        {label || path}
      </span>
      <ExternalLink size={11} className="opacity-60 group-hover:opacity-100 shrink-0"/>
      {label && (
        <span className="text-[10px] text-[#8A8A8A] truncate">{path}</span>
      )}
    </a>
  );
}
